/**
 * Reading GA4 and Matomo identifiers out of their own first-party cookies.
 *
 * BEHAVIOURAL RULE 5 lives here. A GA4 Measurement Protocol event carries no attribution
 * of its own: GA4 attributes it to the session identified by client_id + session_id.
 * Without a session_id the event lands in a brand new (direct) session, which is worse
 * than not sending it -- it manufactures a direct conversion that never happened.
 *
 * The trap is that the _ga_<STREAM_ID> cookie has two incompatible formats in the wild:
 *
 *   GS1.1.1699999999.5.1.1699999999.60.0.0
 *   GS2.1.s1749040000$o5$g1$t1749040100$j60$l0$h0
 *
 * A parser written against only the first shape returns "1" or undefined for the second
 * -- not an error, just a wrong number -- and produces exactly the orphan session this
 * rule exists to prevent. Both shapes are handled, both are tested, and anything that
 * does not parse cleanly is reported as ABSENT rather than guessed at, so the caller can
 * skip the send.
 */

/**
 * Extracts the GA4 client ID from a _ga cookie value.
 *
 * Format: GA1.<domainDepth>.<random>.<firstSeen>, and the client ID GA4 expects is the
 * last two components joined -- not the whole cookie value.
 *
 * @param {string} value Raw _ga cookie value.
 * @return {string|null} Client ID, or null when it cannot be parsed.
 */
export function parseGaClientId( value ) {
	const raw = String( value || '' ).trim();

	if ( ! raw ) {
		return null;
	}

	const parts = raw.split( '.' );

	// GA1.<depth>.<a>.<b> -- anything shorter is not a client ID.
	if ( parts.length < 4 || ! /^GA\d+$/i.test( parts[ 0 ] ) ) {
		return null;
	}

	const clientId = `${ parts[ parts.length - 2 ] }.${ parts[ parts.length - 1 ] }`;

	return /^\d+\.\d+$/.test( clientId ) ? clientId : null;
}

/**
 * Extracts the GA4 session ID from a _ga_<STREAM_ID> cookie value.
 *
 * Handles both documented shapes. Returns null -- meaning "absent", not "zero" -- for
 * anything else, including a value that is structurally right but whose session ID is not
 * a timestamp.
 *
 * @param {string} value Raw _ga_<STREAM_ID> cookie value.
 * @return {string|null} Session ID, or null when it cannot be parsed.
 */
export function parseGaSessionId( value ) {
	const raw = String( value || '' ).trim();

	if ( ! raw ) {
		return null;
	}

	const parts = raw.split( '.' );
	const version = ( parts[ 0 ] || '' ).toUpperCase();

	// Newer shape: GS2.1.s<sessionId>$o<n>$g<n>$t<ts>$j<n>$l<n>$h<n>
	// The $-delimited segments are order-independent in practice, so the s-segment is
	// searched for by prefix rather than taken by position.
	if ( version === 'GS2' ) {
		const payload = parts.slice( 2 ).join( '.' );

		for ( const segment of payload.split( '$' ) ) {
			if ( segment.startsWith( 's' ) ) {
				const candidate = segment.slice( 1 );
				return /^\d+$/.test( candidate ) ? candidate : null;
			}
		}

		return null;
	}

	// Original shape: GS1.1.<sessionId>.<sessionNumber>....
	if ( version === 'GS1' ) {
		const candidate = parts[ 2 ];
		return candidate && /^\d+$/.test( candidate ) ? candidate : null;
	}

	return null;
}

/**
 * Extracts the Matomo visitor ID from a _pk_id.* cookie value.
 *
 * Format: <visitorId>.<firstVisitTs>.<...>, where visitorId is 16 hex characters.
 *
 * Matomo does not reject a malformed visitor ID -- it creates a NEW visitor for it. That
 * is worse than not sending, because it silently inflates the visitor count and detaches
 * the goal conversion from the real visit, so the value is validated before it is used.
 *
 * @param {string} value Raw _pk_id.* cookie value.
 * @return {string|null} Visitor ID, or null when it cannot be parsed.
 */
export function parseMatomoVisitorId( value ) {
	const raw = String( value || '' ).trim();

	if ( ! raw ) {
		return null;
	}

	const candidate = raw.split( '.' )[ 0 ];

	return isValidMatomoVisitorId( candidate ) ? candidate.toLowerCase() : null;
}

/**
 * Whether a string is a well-formed Matomo visitor ID.
 *
 * @param {string} value Candidate visitor ID.
 * @return {boolean} True when it is 16 hex characters.
 */
export function isValidMatomoVisitorId( value ) {
	return /^[a-f0-9]{16}$/i.test( String( value || '' ) );
}

/**
 * Reads a cookie by exact name.
 *
 * @param {string} name       Cookie name.
 * @param {string} cookieString Raw document.cookie value.
 * @return {string} Cookie value, or an empty string.
 */
export function readCookie( name, cookieString ) {
	const source = String( cookieString == null ? '' : cookieString );

	for ( const pair of source.split( ';' ) ) {
		const index = pair.indexOf( '=' );

		if ( index === -1 ) {
			continue;
		}

		if ( pair.slice( 0, index ).trim() === name ) {
			return decodeURIComponent( pair.slice( index + 1 ).trim() );
		}
	}

	return '';
}

/**
 * Reads the first cookie whose name matches a prefix.
 *
 * The GA4 session cookie is named _ga_<STREAM_ID> and the Matomo visitor cookie is named
 * _pk_id.<siteId>.<hash>; neither name is known ahead of time from the browser's side.
 *
 * @param {string} prefix       Cookie name prefix.
 * @param {string} cookieString Raw document.cookie value.
 * @return {string} Cookie value, or an empty string.
 */
export function readCookieByPrefix( prefix, cookieString ) {
	const source = String( cookieString == null ? '' : cookieString );

	for ( const pair of source.split( ';' ) ) {
		const index = pair.indexOf( '=' );

		if ( index === -1 ) {
			continue;
		}

		if ( pair.slice( 0, index ).trim().startsWith( prefix ) ) {
			return decodeURIComponent( pair.slice( index + 1 ).trim() );
		}
	}

	return '';
}

/**
 * Collects the analytics identifiers available in this browser.
 *
 * Every field is either a validated value or an empty string. An empty ga_session_id is
 * the signal for the GA4 destination to skip the send entirely rather than emit an event
 * that will be attributed to a fresh (direct) session.
 *
 * @param {string} cookieString Raw document.cookie value.
 * @return {{ga_client_id: string, ga_session_id: string, matomo_visitor: string}} Identifiers.
 */
export function collectAnalyticsIds( cookieString ) {
	return {
		ga_client_id: parseGaClientId( readCookie( '_ga', cookieString ) ) || '',
		ga_session_id: parseGaSessionId( readCookieByPrefix( '_ga_', cookieString ) ) || '',
		matomo_visitor: parseMatomoVisitorId( readCookieByPrefix( '_pk_id.', cookieString ) ) || '',
	};
}
